import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  ContentManagement,
  ContentManagementVersion,
  ContentManagementTranslation,
  ContentManagementState,
  ContentManagementResponse,
  ContentManagementVersionResponse,
  ContentManagementTranslationResponse,
} from "../../modules/shared/model/ContentManagementInterfaces";
import {
  createContentManagementVersion,
  getAllContentManagementVersions,
  getContentManagementVersionById,
  updateContentManagementVersion,
  deleteContentManagementVersion,
  createContentManagement,
  getAllContentManagements,
  getContentManagementById,
  updateContentManagement,
  deleteContentManagement,
  createContentManagementTranslation,
  getAllContentManagementTranslations,
  getContentManagementTranslationById,
  updateContentManagementTranslation,
  deleteContentManagementTranslation,
  getTranslationsByContentType,
  getContentManagementByType,
  getContentManagementByVersionId,
  getContentManagementTranslationsByContentId,
} from "../Actions/ContentManagementAction";

const initialState: ContentManagementState = {
  versions: [],
  contentManagements: [],
  translations: [],
  loading: false,
  error: null,
  selectedVersion: null,
  selectedContent: null,
  selectedTranslation: null,
};

const contentManagementSlice = createSlice({
  name: "contentManagement",
  initialState,
  reducers: {
    // Clear selected items
    clearSelectedVersion: (state) => {
      state.selectedVersion = null;
    },
    clearSelectedContent: (state) => {
      state.selectedContent = null;
    },
    clearSelectedTranslation: (state) => {
      state.selectedTranslation = null;
    },
    clearAllSelected: (state) => {
      state.selectedVersion = null;
      state.selectedContent = null;
      state.selectedTranslation = null;
    },
    // Set selected items
    setSelectedVersion: (state, action: PayloadAction<ContentManagementVersion | null>) => {
      state.selectedVersion = action.payload;
    },
    setSelectedContent: (state, action: PayloadAction<ContentManagement | null>) => {
      state.selectedContent = action.payload;
    },
    setSelectedTranslation: (state, action: PayloadAction<ContentManagementTranslation | null>) => {
      state.selectedTranslation = action.payload;
    },
    // Clear error
    clearError: (state) => {
      state.error = null;
    },
    // Reset state
    resetContentManagementState: (state) => {
      state.versions = [];
      state.contentManagements = [];
      state.translations = [];
      state.selectedVersion = null;
      state.selectedContent = null;
      state.selectedTranslation = null;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Content Management Version reducers
    builder
      .addCase(createContentManagementVersion.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createContentManagementVersion.fulfilled, (state, action) => {
        state.loading = false;
        state.versions.push(action.payload);
        state.error = null;
      })
      .addCase(createContentManagementVersion.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(getAllContentManagementVersions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllContentManagementVersions.fulfilled, (state, action) => {
        state.loading = false;
        state.versions = action.payload.data;
        state.error = null;
      })
      .addCase(getAllContentManagementVersions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(getContentManagementVersionById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getContentManagementVersionById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedVersion = action.payload;
        state.error = null;
      })
      .addCase(getContentManagementVersionById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(updateContentManagementVersion.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateContentManagementVersion.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.versions.findIndex(v => v.id === action.payload.id);
        if (index !== -1) {
          state.versions[index] = action.payload;
        }
        if (state.selectedVersion?.id === action.payload.id) {
          state.selectedVersion = action.payload;
        }
        state.error = null;
      })
      .addCase(updateContentManagementVersion.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(deleteContentManagementVersion.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteContentManagementVersion.fulfilled, (state, action) => {
        state.loading = false;
        state.versions = state.versions.filter(v => v.id !== action.payload.id);
        if (state.selectedVersion?.id === action.payload.id) {
          state.selectedVersion = null;
        }
        state.error = null;
      })
      .addCase(deleteContentManagementVersion.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Content Management reducers
    builder
      .addCase(createContentManagement.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createContentManagement.fulfilled, (state, action) => {
        state.loading = false;
        // Ensure contentManagements is an array and handle different response structures
        if (Array.isArray(state.contentManagements)) {
          // If the payload is an array, spread it; if it's a single item, push it
          if (Array.isArray(action.payload)) {
            state.contentManagements.push(...action.payload);
          } else if (action.payload) {
            state.contentManagements.push(action.payload);
          }
        } else {
          // If contentManagements is not an array, initialize it
          state.contentManagements = action.payload ? [action.payload] : [];
        }
        state.error = null;
      })
      .addCase(createContentManagement.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(getAllContentManagements.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllContentManagements.fulfilled, (state, action) => {
        state.loading = false;
        // Ensure we always have an array
        if (action.payload && action.payload.data) {
          state.contentManagements = Array.isArray(action.payload.data) ? action.payload.data : [];
        } else if (Array.isArray(action.payload)) {
          state.contentManagements = action.payload;
        } else {
          state.contentManagements = [];
        }
        state.error = null;
      })
      .addCase(getAllContentManagements.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(getContentManagementById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getContentManagementById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedContent = action.payload;
        state.error = null;
      })
      .addCase(getContentManagementById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(updateContentManagement.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateContentManagement.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.contentManagements.findIndex(c => c.id === action.payload.id);
        if (index !== -1) {
          state.contentManagements[index] = action.payload;
        }
        if (state.selectedContent?.id === action.payload.id) {
          state.selectedContent = action.payload;
        }
        state.error = null;
      })
      .addCase(updateContentManagement.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(deleteContentManagement.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteContentManagement.fulfilled, (state, action) => {
        state.loading = false;
        state.contentManagements = state.contentManagements.filter(c => c.id !== action.payload.id);
        if (state.selectedContent?.id === action.payload.id) {
          state.selectedContent = null;
        }
        state.error = null;
      })
      .addCase(deleteContentManagement.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(getContentManagementByType.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getContentManagementByType.fulfilled, (state, action) => {
        state.loading = false;
        // Ensure we always have an array
        state.contentManagements = Array.isArray(action.payload) ? action.payload : [];
        state.error = null;
      })
      .addCase(getContentManagementByType.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(getContentManagementByVersionId.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getContentManagementByVersionId.fulfilled, (state, action) => {
        state.loading = false;
        // Ensure we always have an array
        state.contentManagements = Array.isArray(action.payload) ? action.payload : [];
        state.error = null;
      })
      .addCase(getContentManagementByVersionId.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(getContentManagementTranslationsByContentId.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getContentManagementTranslationsByContentId.fulfilled, (state, action) => {
        state.loading = false;
        state.translations = action.payload;
        state.error = null;
      })
      .addCase(getContentManagementTranslationsByContentId.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Content Management Translation reducers
    builder
      .addCase(createContentManagementTranslation.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createContentManagementTranslation.fulfilled, (state, action) => {
        state.loading = false;
        state.translations.push(action.payload);
        state.error = null;
      })
      .addCase(createContentManagementTranslation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(getAllContentManagementTranslations.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllContentManagementTranslations.fulfilled, (state, action) => {
        state.loading = false;
        state.translations = action.payload.data;
        state.error = null;
      })
      .addCase(getAllContentManagementTranslations.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(getContentManagementTranslationById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getContentManagementTranslationById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedTranslation = action.payload;
        state.error = null;
      })
      .addCase(getContentManagementTranslationById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(updateContentManagementTranslation.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateContentManagementTranslation.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.translations.findIndex(t => t.id === action.payload.id);
        if (index !== -1) {
          state.translations[index] = action.payload;
        }
        if (state.selectedTranslation?.id === action.payload.id) {
          state.selectedTranslation = action.payload;
        }
        state.error = null;
      })
      .addCase(updateContentManagementTranslation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(deleteContentManagementTranslation.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteContentManagementTranslation.fulfilled, (state, action) => {
        state.loading = false;
        state.translations = state.translations.filter(t => t.id !== action.payload.id);
        if (state.selectedTranslation?.id === action.payload.id) {
          state.selectedTranslation = null;
        }
        state.error = null;
      })
      .addCase(deleteContentManagementTranslation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(getTranslationsByContentType.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getTranslationsByContentType.fulfilled, (state, action) => {
        state.loading = false;
        state.translations = action.payload;
        state.error = null;
      })
      .addCase(getTranslationsByContentType.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  clearSelectedVersion,
  clearSelectedContent,
  clearSelectedTranslation,
  clearAllSelected,
  setSelectedVersion,
  setSelectedContent,
  setSelectedTranslation,
  clearError,
  resetContentManagementState,
} = contentManagementSlice.actions;

export default contentManagementSlice.reducer; 